// Wholesales hub — one outlet panel with four tabs (desktop Wholesales menu
// parity):
//   Sales Entry | Wholesales Invoices | On Hold Invoices | Summary
//
// Reuses the shared trade children with wholesale configuration:
//   <pos-entry mode="wholesale">            — cost-based pricing, credit terms
//   <sales-records kind="sales|hold" mode="credit">
//   <summary-report kind="sales-credit">
//
// Recall/Edit: on-hold rows dispatch "trade-recall" (the hold is deleted as
// soon as it loads into the entry, desktop parity); posted invoices dispatch
// "trade-edit" and the entry saves back to the same invoice id.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { TradeSalesRow } from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import './pos-entry.js';
import './sales-records.js';
import './summary-report.js';

type WholesalesTab = 'entry' | 'invoices' | 'hold' | 'summary';

interface KeptView {
  tab: WholesalesTab;
}

@customElement('wholesales-page')
export class WholesalesPage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .tabs { margin: 0.75rem 1rem 0; margin-bottom: 0; }
    .pane { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .pane > * { flex: 1; min-height: 0; }
  `];

  @property({ type: String }) storeCode = '';

  @state() private _tab: WholesalesTab = 'entry';
  @state() private _recall: { row: TradeSalesRow; token: number; edit?: boolean } | null = null;
  private _recallSeq = 0;

  private _view = new OutletViewCache<KeptView>(this, 'wholesales-page', {
    // pos-entry keeps its own per-outlet view (lines + edit id); a stale
    // recall must not overwrite that restored screen.
    capture: () => ({ tab: this._tab }),
    restore: (v) => {
      this._tab = v.tab;
      this._recall = null;
    },
    reset: () => {
      this._tab = 'entry';
      this._recall = null;
    },
  });

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated() {
    this._view.switchTo(this.storeCode);
  }

  private _onRecall = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      { tradeKind: string; row: TradeSalesRow; edit?: boolean } | undefined;
    if (!detail || detail.tradeKind !== 'sale' || !detail.row) return;
    this._recallSeq += 1;
    this._recall = { row: detail.row, token: this._recallSeq, edit: !!detail.edit };
    this._tab = 'entry';
  };

  private _tabLabel(id: WholesalesTab): string {
    switch (id) {
      case 'entry': return 'Sales Entry';
      case 'invoices': return 'Wholesales Invoices';
      case 'hold': return 'On Hold Invoices';
      default: return 'Summary';
    }
  }

  render() {
    if (!this.storeCode) return html`<div style="padding:2rem;color:var(--color-text-secondary)">No outlet selected.</div>`;
    const tabs: WholesalesTab[] = ['entry', 'invoices', 'hold', 'summary'];
    return html`
      <div class="tabs">
        ${tabs.map((t) => html`
          <button class=${this._tab === t ? 'active' : ''}
            @click=${() => { if (t !== 'entry') this._recall = null; this._tab = t; }}>
            ${this._tabLabel(t)}
          </button>
        `)}
      </div>
      <div class="pane" @trade-recall=${this._onRecall} @trade-edit=${this._onRecall}>
        ${this._tab === 'entry' ? html`
          <pos-entry
            .storeCode=${this.storeCode}
            .mode=${'wholesale'}
            .recallRow=${this._recall?.row ?? null}
            .recallToken=${this._recall?.token ?? 0}
            .editMode=${this._recall?.edit ?? false}
          ></pos-entry>
        ` : ''}
        ${this._tab === 'invoices' ? html`
          <sales-records .storeCode=${this.storeCode} .kind=${'sales'} .mode=${'credit'}></sales-records>
        ` : ''}
        ${this._tab === 'hold' ? html`
          <sales-records .storeCode=${this.storeCode} .kind=${'hold'} .mode=${'credit'}></sales-records>
        ` : ''}
        ${this._tab === 'summary' ? html`
          <summary-report .storeCode=${this.storeCode} .kind=${'sales-credit'}></summary-report>
        ` : ''}
      </div>
    `;
  }
}
